import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { canInstall, onInstallAvailabilityChange, promptInstall } from '../services/install-prompt.js';

/**
 * A home-page "Install as app" call-out that highlights offline play. It only appears when the browser
 * has offered an install prompt (`beforeinstallprompt`), i.e. the app is installable and not already
 * installed; otherwise it renders nothing. Clicking the button triggers the browser's install dialog
 * via {@link promptInstall} (which must run from this user gesture).
 */
@customElement('gc-install-prompt')
export class GcInstallPrompt extends LitElement {
  static override styles = css`
    :host { display: block; }
    .install {
      display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;
      margin: 0 0 1.25rem; padding: 1rem 1.25rem; border-radius: var(--gc-radius-lg, 1rem);
      background: linear-gradient(135deg, rgba(20, 81, 42, 0.06), rgba(42, 157, 63, 0.12));
      border: 1px solid var(--gc-border, #d8e0d8);
    }
    .icon {
      width: 2.75rem; height: 2.75rem; flex: none; border-radius: 0.7rem; display: grid; place-items: center;
      background: linear-gradient(135deg, #14512a, #2a9d3f); color: #fff; font-size: 1.4rem;
    }
    .copy { display: flex; flex-direction: column; gap: 0.15rem; min-width: 12rem; flex: 1 1 14rem; }
    .title { font-weight: 700; }
    .subtitle { color: var(--gc-ink-soft, #5b6b61); font-size: 0.9rem; }
    button {
      margin-left: auto; padding: 0.6rem 1.2rem; border: none; border-radius: 0.55rem; cursor: pointer;
      font-size: 1rem; font-weight: 600; color: #fff;
      background: linear-gradient(135deg, var(--gc-accent-strong, #1f7d30), var(--gc-accent, #2a9d3f));
      box-shadow: var(--gc-shadow);
    }
    button:hover { filter: brightness(1.06); }
  `;

  @state() private available = canInstall();
  private unsubscribe?: () => void;

  override connectedCallback(): void {
    super.connectedCallback();
    this.available = canInstall();
    this.unsubscribe = onInstallAvailabilityChange((available) => {
      this.available = available;
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  override render() {
    if (!this.available) {
      return nothing;
    }
    return html`
      <section class="install" aria-label="Install Gnome Cards">
        <span class="icon" aria-hidden="true">⬇</span>
        <span class="copy">
          <span class="title">Install as app</span>
          <span class="subtitle">Add Gnome Cards to your device to play offline anytime.</span>
        </span>
        <button @click=${this.onInstall}>Install as app</button>
      </section>
    `;
  }

  private onInstall = async (): Promise<void> => {
    await promptInstall();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-install-prompt': GcInstallPrompt;
  }
}
